import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { BILLING_CONFIG, ADVANCED_CONFIG } from '../config';
import { useAuth } from './auth';
import { api } from '../utils/api';
import { createPendingPaymentSubscription } from '../utils/billingSubscription';
import { trackPaymentCompleted } from '../utils/billingFunnel';
import { confirmPaymentIntent } from '../utils/paymentConfirmation';

// Initialize Stripe
let stripePromise = null;
if (BILLING_CONFIG.ENABLED && BILLING_CONFIG.STRIPE.PUBLISHABLE_KEY) {
  stripePromise = loadStripe(BILLING_CONFIG.STRIPE.PUBLISHABLE_KEY);
}

// Billing Context
const BillingContext = createContext(null);

export const useBilling = () => {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
};

// Usage tracking hook
export const useUsageTracking = () => {
  const [usage, setUsage] = useState(() => {
    const stored = localStorage.getItem(ADVANCED_CONFIG.STORAGE.USAGE_STATS_KEY);
    return stored ? JSON.parse(stored) : {
      daily: { count: 0, date: new Date().toDateString() },
      monthly: { count: 0, month: new Date().getMonth(), year: new Date().getFullYear() }
    };
  });

  const incrementUsage = useCallback(() => {
    const now = new Date();
    const today = now.toDateString();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    setUsage(prev => {
      const newUsage = { ...prev };

      // Reset daily count if new day
      if (newUsage.daily.date !== today) {
        newUsage.daily = { count: 1, date: today };
      } else {
        newUsage.daily.count += 1;
      }

      // Reset monthly count if new month
      if (newUsage.monthly.month !== currentMonth || newUsage.monthly.year !== currentYear) {
        newUsage.monthly = { count: 1, month: currentMonth, year: currentYear };
      } else {
        newUsage.monthly.count += 1;
      }

      // Save to localStorage
      localStorage.setItem(ADVANCED_CONFIG.STORAGE.USAGE_STATS_KEY, JSON.stringify(newUsage));

      return newUsage;
    });
  }, []);

  const checkQuota = useCallback((plan = 'free') => {
    if (plan === 'free') {
      const dailyLimit = BILLING_CONFIG.PRICING.FREE_TIER.REQUESTS_PER_DAY;
      const monthlyLimit = BILLING_CONFIG.PRICING.FREE_TIER.REQUESTS_PER_MONTH;

      return {
        canUse: usage.daily.count < dailyLimit && usage.monthly.count < monthlyLimit,
        dailyRemaining: Math.max(0, dailyLimit - usage.daily.count),
        monthlyRemaining: Math.max(0, monthlyLimit - usage.monthly.count),
        dailyUsed: usage.daily.count,
        monthlyUsed: usage.monthly.count,
        dailyLimit,
        monthlyLimit
      };
    }

    // For paid plans, assume unlimited or higher limits
    return {
      canUse: true,
      dailyRemaining: Infinity,
      monthlyRemaining: Infinity,
      dailyUsed: usage.daily.count,
      monthlyUsed: usage.monthly.count,
      dailyLimit: Infinity,
      monthlyLimit: Infinity
    };
  }, [usage]);

  return {
    usage,
    incrementUsage,
    getQuotaInfo: checkQuota
  };
};

// Billing Provider
const BillingProvider = ({
  children,
  stripe = null,
  elements = null,
  setPaymentClientSecret = () => {},
}) => {
  const { creator, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get user plan
  const getCurrentPlan = useCallback(() => {
    if (!isAuthenticated || !creator) return 'free';
    return creator.plan || 'free';
  }, [isAuthenticated, creator]);

  const createSubscription = useCallback(async (planId) => {
    if (!BILLING_CONFIG.ENABLED) {
      throw new Error('Billing is not enabled');
    }

    setLoading(true);
    setError(null);

    try {
      const pendingSubscription = await createPendingPaymentSubscription({
        planId,
        createPaymentIntent: api.createPaymentIntent.bind(api),
        trackFunnelEvent: api.trackFunnelEvent.bind(api),
        userId: creator?.id,
        currency: BILLING_CONFIG.STRIPE.CURRENCY,
      });
      setSubscription(pendingSubscription);
      setPaymentClientSecret(pendingSubscription.clientSecret || null);
      console.log('Payment intent created:', pendingSubscription);

      return pendingSubscription;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [creator, setPaymentClientSecret]);

  const confirmSubscriptionPayment = useCallback(async (returnUrl) => {
    if (!subscription?.clientSecret) {
      throw new Error('No pending payment intent to confirm');
    }

    setLoading(true);
    setError(null);

    try {
      const paymentIntent = await confirmPaymentIntent({
        stripe,
        elements,
        clientSecret: subscription.clientSecret,
        returnUrl: returnUrl || (typeof window !== 'undefined' ? window.location.href : '/'),
      });
      const confirmedSubscription = {
        ...subscription,
        status: paymentIntent?.status || subscription.status,
        paymentIntentId: paymentIntent?.id || subscription.paymentIntentId,
      };

      setSubscription(confirmedSubscription);

      if (paymentIntent?.status === 'succeeded') {
        void trackPaymentCompleted(api.trackFunnelEvent.bind(api), {
          userId: creator?.id,
          subscription: confirmedSubscription,
          surface: 'subscription_checkout',
        });
      }

      return paymentIntent;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [creator, elements, stripe, subscription]);

  const cancelSubscription = useCallback(async () => {
    if (!subscription) return;

    setLoading(true);
    setError(null);

    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSubscription(prev => ({
        ...prev,
        cancelAtPeriodEnd: true
      }));

      console.log('Subscription cancelled');
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [subscription]);

  const addPaymentMethod = useCallback(async (paymentMethodData) => {
    setLoading(true);
    setError(null);

    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const newPaymentMethod = {
        id: `pm_${Date.now()}`,
        type: paymentMethodData.type,
        card: paymentMethodData.card,
        isDefault: paymentMethods.length === 0
      };

      setPaymentMethods(prev => [...prev, newPaymentMethod]);

      return newPaymentMethod;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [paymentMethods]);

  const removePaymentMethod = useCallback(async (paymentMethodId) => {
    setLoading(true);
    setError(null);

    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      setPaymentMethods(prev => prev.filter(pm => pm.id !== paymentMethodId));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get plan info
  const getPlanInfo = useCallback((planId) => {
    return BILLING_CONFIG.PRICING.PLANS.find(plan => plan.id === planId) || null;
  }, []);

  const value = {
    isEnabled: BILLING_CONFIG.ENABLED,
    currentPlan: getCurrentPlan(),
    subscription,
    paymentMethods,
    loading,
    error,
    plans: BILLING_CONFIG.PRICING.PLANS,
    createSubscription,
    confirmSubscriptionPayment,
    cancelSubscription,
    addPaymentMethod,
    removePaymentMethod,
    getPlanInfo,
    freeTierLimits: BILLING_CONFIG.PRICING.FREE_TIER
  };

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
};

const StripeBillingProvider = ({ children, setPaymentClientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();

  return (
    <BillingProvider
      stripe={stripe}
      elements={elements}
      setPaymentClientSecret={setPaymentClientSecret}
    >
      {children}
    </BillingProvider>
  );
};

// Stripe Provider wrapper
export const StripeProvider = ({ children }) => {
  if (!BILLING_CONFIG.ENABLED || !stripePromise) {
    return <BillingProvider>{children}</BillingProvider>;
  }

  const [paymentClientSecret, setPaymentClientSecret] = useState(null);
  const options = useMemo(() => {
    const stripeOptions = {
      fonts: [
        {
          cssSrc: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap',
        },
      ],
    };
    if (paymentClientSecret) {
      stripeOptions.clientSecret = paymentClientSecret;
    }
    return stripeOptions;
  }, [paymentClientSecret]);

  return (
    <Elements stripe={stripePromise} options={options}>
      <StripeBillingProvider setPaymentClientSecret={setPaymentClientSecret}>
        {children}
      </StripeBillingProvider>
    </Elements>
  );
};

export { BillingProvider };
